import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './App.css';

function App() {
  const [sessionId, setSessionId] = useState('');
  const [userId, setUserId] = useState('');
  const [username, setUsername] = useState('');
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [sessionTitle, setSessionTitle] = useState('');
  const [session, setSession] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question: '',
    options: ['', '', '', ''],
    answer: 0,
    triviaMode: false
  });
  const [aiTopic, setAiTopic] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiGenerationKey, setAiGenerationKey] = useState(0);
  const [showAboutModal, setShowAboutModal] = useState(false);

  // Refs for ReactQuill editors
  const questionQuillRef = useRef(null);
  const optionQuillRefs = useRef([null, null, null, null]);

  // Debug question form state changes
  useEffect(() => {
    console.log('Question form state updated:', questionForm);
  }, [questionForm]);

  // Store user's quiz sessions in localStorage
  const [userSessions, setUserSessions] = useState(() => {
    const saved = localStorage.getItem('userQuizSessions');
    return saved ? JSON.parse(saved) : [];
  });

  // Add current session to user sessions
  const addCurrentSessionToHistory = () => {
    if (sessionId && session) {
      const newSession = {
        id: sessionId,
        title: session.title || 'New Session',
        createdAt: session.createdAt,
        lastAccessed: new Date().toISOString()
      };

      const existingSessions = userSessions.filter(s => s.id !== sessionId);
      const updatedSessions = [newSession, ...existingSessions];

      setUserSessions(updatedSessions);
      localStorage.setItem('userQuizSessions', JSON.stringify(updatedSessions));
    }
  };

  // Remove session from user sessions
  const removeSessionFromHistory = (sessionIdToRemove) => {
    const updatedSessions = userSessions.filter(s => s.id !== sessionIdToRemove);
    setUserSessions(updatedSessions);
    localStorage.setItem('userQuizSessions', JSON.stringify(updatedSessions));
  };

  // Quill modules with hidden toolbar for question (to keep it clean)
  const quillModules = {
    toolbar: false, // Hide toolbar completely
    keyboard: {
      bindings: {
        // Keep default keyboard shortcuts
        bold: {
          key: 'b',
          ctrlKey: true,
          shiftKey: false,
          altKey: false,
          metaKey: false
        },
        italic: {
          key: 'i',
          ctrlKey: true,
          shiftKey: false,
          altKey: false,
          metaKey: false
        },
        underline: {
          key: 'u',
          ctrlKey: true,
          shiftKey: false,
          altKey: false,
          metaKey: false
        }
      }
    },
    clipboard: {
      matchVisual: false
    }
  };

  // Quill modules for answer options with toolbar enabled for editing
  const quillModulesWithOptions = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['clean']
    ],
    keyboard: {
      bindings: {
        // Keep default keyboard shortcuts
        bold: {
          key: 'b',
          ctrlKey: true,
          shiftKey: false,
          altKey: false,
          metaKey: false
        },
        italic: {
          key: 'i',
          ctrlKey: true,
          shiftKey: false,
          altKey: false,
          metaKey: false
        },
        underline: {
          key: 'u',
          ctrlKey: true,
          shiftKey: false,
          altKey: false,
          metaKey: false
        }
      }
    }
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'script', 'align', 'color', 'background',
    'link', 'image'
  ];
  const ws = useRef(null);

  useEffect(() => {
    // Check if user ID already exists in localStorage, otherwise generate a new one
    const existingUserId = localStorage.getItem('persistentUserId');
    if (existingUserId) {
      setUserId(existingUserId);
    } else {
      const newUserId = 'user_' + Math.random().toString(36).substr(2, 9);
      setUserId(newUserId);
      localStorage.setItem('persistentUserId', newUserId);
    }
  }, []);

  useEffect(() => {
    // Check for session ID in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const sessionFromUrl = urlParams.get('session');
    if (sessionFromUrl) {
      setSessionId(sessionFromUrl);
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      connectWebSocket();
    }

    // About Modal Component
  const AboutModal = () => {
    if (!showAboutModal) return null;
    return (
      <div className='modal-overlay' onClick={() => setShowAboutModal(false)} style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
        <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{background: 'white', borderRadius: '10px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto'}}>
          <div className='modal-header'>
            <h2>About PulseRound</h2>
            <button className='modal-close' onClick={() => setShowAboutModal(false)} aria-label='Close'>�</button>
          </div>
          <div className='modal-body'>
            <div className='about-section'>
              <h3>What is PulseRound?</h3>
              <p>PulseRound is a real-time collaborative quiz platform that lets groups create, answer, and discuss questions together. Perfect for classrooms, team meetings, workshops, and social gatherings.</p>
            </div>
            <div className='about-section'>
              <h3>How to Use</h3>
              <div className='usage-steps'>
                <div className='step'>
                  <h4>?? Create a Session</h4>
                  <p>Start a new quiz session and share the session ID with your group.</p>
                </div>
                <div className='step'>
                  <h4>?? Add Questions</h4>
                  <p>Create multiple-choice questions manually or generate them automatically with different tones.</p>
                </div>
                <div className='step'>
                  <h4>?? Join & Answer</h4>
                  <p>Participants join using the session ID and answer questions in real-time.</p>
                </div>
                <div className='step'>
                  <h4>?? Reveal Results</h4>
                  <p>The facilitator can reveal answer counts and correct questions when ready.</p>
                </div>
                <div className='step'>
                  <h4>?? View Analytics</h4>
                  <p>See visual charts of group responses and detailed breakdowns.</p>
                </div>
              </div>
            </div>
            <div className='about-section'>
              <h3>Key Features</h3>
              <ul className='feature-list'>
                <li><strong>Real-time Collaboration:</strong> See responses as they happen</li>
                <li><strong>Automatic Question Generation:</strong> Create questions with different tones (humorous, factual, engaging, challenging)</li>
                <li><strong>Per-Question Control:</strong> Reveal answers for individual questions when you're ready</li>
                <li><strong>Visual Analytics:</strong> Charts and graphs showing group responses</li>
                <li><strong>No Login Required:</strong> Anyone can join with just a session ID</li>
                <li><strong>Mobile Friendly:</strong> Works perfectly on phones, tablets, and desktops</li>
              </ul>
            </div>
            <div className='about-section'>
              <h3>Perfect For</h3>
              <div className='use-cases'>
                <div className='use-case'>
                  <h4>?? Education</h4>
                  <p>Classroom quizzes, knowledge checks, and interactive learning</p>
                </div>
                <div className='use-case'>
                  <h4>?? Business</h4>
                  <p>Team meetings, training sessions, and feedback collection</p>
                </div>
                <div className='use-case'>
                  <h4>?? Events</h4>
                  <p>Icebreakers, audience engagement, and interactive presentations</p>
                </div>
                <div className='use-case'>
                  <h4>?? Social</h4>
                  <p>Family game nights, friend gatherings, and party games</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [sessionId]);

  // Add session to history when session state changes
  useEffect(() => {
    if (session && sessionId) {
      addCurrentSessionToHistory();
    }
  }, [session, sessionId]);

  // Update username from session data when session changes
  useEffect(() => {
    console.log('Username sync useEffect triggered:', { session, userId, currentUsername: username });
    if (session && session.usernames && userId) {
      const sessionUsername = session.usernames[userId];
      console.log('Username from session:', sessionUsername, 'Current username:', username);
      if (sessionUsername && sessionUsername !== username) {
        console.log('Updating username from session data:', sessionUsername);
        setUsername(sessionUsername);
      }
    }
  }, [session, userId]);

  const connectWebSocket = () => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/ws`;

    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      setConnected(true);
      setError('');

      // Join the session
      ws.current.send(JSON.stringify({
        type: 'join_session',
        sessionId: sessionId
      }));
    };

    ws.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      console.log('WebSocket message received:', message.type, message.data);

      if (message.type === 'session_state' || message.type === 'answer_submitted' ||
        message.type === 'question_added' || message.type === 'question_updated' || message.type === 'question_deleted' || message.type === 'session_updated') {
        console.log('Setting session data:', message.data);
        setSession(message.data);
      }
    };

    ws.current.onclose = () => {
      setConnected(false);
    };

    ws.current.onerror = () => {
      setError('WebSocket connection error');
      setConnected(false);
    };
  };

  const createSession = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          creatorId: userId,
          title: sessionTitle || 'Untitled Quiz Session'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create session');
      }

      const data = await response.json();
      console.log('createSession: got session data', data);
      setSessionId(data.id);
      setSession(data);
      setSessionTitle(data.title);
    } catch (err) {
      setError('Failed to create session: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const joinSession = async () => {
    if (!sessionId.trim()) {
      setError('Please enter a session ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/session/${sessionId}`);

      if (!response.ok) {
        throw new Error('Session not found');
      }

      const data = await response.json();
      console.log('joinSession: got session data', data);
      setSession(data);
    } catch (err) {
      setError('Failed to join session: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async (questionId, answerIndex) => {
    if (!sessionId || !userId) {
      setError('Session or user ID missing');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('username', username || userId);
      formData.append('questionId', questionId.toString());
      formData.append('answerIndex', answerIndex.toString());

      const response = await fetch(`/api/session/${sessionId}/answer`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit answer');
      }

      // Update local state - if user unselected, remove from selectedAnswers
      const userAnswer = getUserAnswer(questionId);
      if (userAnswer === answerIndex) {
        // User is unselecting
        setSelectedAnswers(prev => {
          const newAnswers = { ...prev };
          delete newAnswers[questionId];
          return newAnswers;
        });
      } else {
        // User is selecting or changing answer
        setSelectedAnswers(prev => ({
          ...prev,
          [questionId]: answerIndex
        }));
      }

    } catch (err) {
      setError('Failed to submit answer: ' + err.message);
    }
  };

  const startEditUsername = () => {
    setIsEditingUsername(true);
    setUsername(username || userId);
  };

  const saveUsername = async () => {
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('username', username || userId);

      const response = await fetch(`/api/session/${sessionId}/username`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to update username');
      }

      setIsEditingUsername(false);
      setError('');
    } catch (err) {
      setError('Failed to update username: ' + err.message);
    }
  };

  const startEditTitle = () => {
    setIsEditingTitle(true);
    setSessionTitle(session?.title || '');
  };

  const saveTitle = async () => {
    if (!sessionId || !userId) return;

    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('title', sessionTitle || 'Untitled Quiz Session');

      const response = await fetch(`/api/session/${sessionId}/title`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to update title');
      }

      const data = await response.json();
      setSession(data);
      setIsEditingTitle(false);
      setError('');
    } catch (err) {
      setError('Failed to update title: ' + err.message);
    }
  };

  const cancelEditTitle = () => {
    setIsEditingTitle(false);
    setSessionTitle(session?.title || '');
  };

  const toggleRevealAnswers = async () => {
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('reveal', (!session?.revealCorrectAnswers).toString());

      const response = await fetch(`/api/session/${sessionId}/reveal-answers`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to toggle answer reveal');
      }

      const data = await response.json();
      setSession(prev => ({ ...prev, revealCorrectAnswers: data.revealCorrectAnswers }));
      setError('');
    } catch (err) {
      setError('Failed to toggle answer reveal: ' + err.message);
    }
  };


  const cancelEditUsername = () => {
    setIsEditingUsername(false);
    setUsername('');
  };

  const generateQuestionWithAI = async () => {
    if (!aiTopic.trim()) {
      setError('Please enter a topic for AI question generation');
      return;
    }

    setIsGeneratingAI(true);
    setError('');

    try {
      const response = await fetch('/api/generate-question', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ topic: aiTopic }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate question');
      }

      const generatedQuestion = await response.json();
      console.log('AI Response:', generatedQuestion);

      // Update question form with AI-generated content
      const newFormData = {
        question: generatedQuestion.question,
        options: generatedQuestion.options,
        answer: generatedQuestion.correctAnswer,
        triviaMode: true // AI-generated questions default to trivia mode
      };
      console.log('New form data:', newFormData);

      // Update form state and force re-render
      console.log('Setting question form with AI data:', newFormData);
      setQuestionForm(newFormData);
      setAiGenerationKey(prev => prev + 1); // Force re-render of ReactQuill components

      setAiTopic('');
      setError('');
    } catch (err) {
      setError('Failed to generate question: ' + err.message);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const getUserAnswer = (questionId) => {
    if (!session || !session.userAnswers) return null;
    return session.userAnswers[questionId] ? session.userAnswers[questionId][userId] : null;
  };

  const getAnswerCounts = (questionId) => {
    if (!session || !session.answers) return {};

    const answers = session.answers[questionId.toString()] || [];
    const counts = {};

    answers.forEach(answer => {
      counts[answer] = (counts[answer] || 0) + 1;
    });

    return counts;
  };

  const getParticipantCount = () => {
    return session ? Object.keys(session.participants || {}).length : 0;
  };

  const getShareableLink = () => {
    if (!sessionId) return '';
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?session=${sessionId}`;
  };

  const isCreator = () => {
    console.log('isCreator check:', {
      sessionCreatorId: session?.creatorId,
      userId: userId,
      areEqual: session?.creatorId === userId,
      result: session?.creatorId === userId
    });
    return session && session.creatorId === userId;
  };

  const addQuestion = async () => {
    if (!questionForm.question.trim()) {
      setError('Please enter a question');
      return;
    }

    if (questionForm.options.slice(0, 2).some(opt => !opt.trim())) {
      setError('Please fill in required answer fields');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('question', questionForm.question);
      formData.append('options', JSON.stringify(questionForm.options));
      formData.append('answer', JSON.stringify(questionForm.answer));
      formData.append('triviaMode', questionForm.triviaMode.toString());

      const response = await fetch(`/api/session/${sessionId}/questions`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to add question');
      }

      // Clear form and hide it after successful submission
      setQuestionForm({ question: '', options: ['', '', '', ''], answer: 0, triviaMode: false });
      setShowQuestionForm(false);
      setError('');
    } catch (err) {
      setError('Failed to add question: ' + err.message);
    }
  };

  const updateQuestion = async () => {
    console.log('Update Question - Current form state:', questionForm);

    if (!questionForm.question.trim() || questionForm.options.some(opt => !opt.trim())) {
      setError('Please fill in all question fields');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('question', questionForm.question);
      formData.append('options', JSON.stringify(questionForm.options));
      formData.append('answer', JSON.stringify(questionForm.answer));
      formData.append('triviaMode', questionForm.triviaMode.toString());
      console.log('Updating question with trivia mode:', questionForm.triviaMode);
      console.log('Form data being sent:', {
        question: questionForm.question,
        options: questionForm.options,
        answer: questionForm.answer,
        triviaMode: questionForm.triviaMode
      });

      const response = await fetch(`/api/session/${sessionId}/questions/${editingQuestion.id}`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Update question failed:', errorData);
        throw new Error(errorData.error || 'Failed to update question');
      }

      const responseData = await response.json();
      console.log('Update question successful response:', responseData);

      setQuestionForm({ question: '', options: ['', '', '', ''], answer: 0, triviaMode: false });
      setEditingQuestion(null);
      setShowQuestionForm(false);
      setError('');
    } catch (err) {
      setError('Failed to update question: ' + err.message);
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('userId', userId);

      const response = await fetch(`/api/session/${sessionId}/questions/${questionId}`, {
        method: 'DELETE',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to delete question');
      }

      setError('');
    } catch (err) {
      setError('Failed to delete question: ' + err.message);
    }
  };

  const startEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      question: question.question,
      options: [...question.options],
      answer: question.answer,
      triviaMode: question.triviaMode || false
    });
    setShowQuestionForm(true);
  };

  const resetQuestionForm = () => {
    setQuestionForm({ question: '', options: ['', '', '', ''], answer: 0, triviaMode: false });
    setEditingQuestion(null);
    setShowQuestionForm(false);
  };

  const getAnswerBreakdown = (questionId) => {
    if (!session || !session.userAnswers || !session.usernames) return [];

    const breakdown = [];
    const userAnswers = session.userAnswers[questionId] || {};

    for (const [userId, answerIndex] of Object.entries(userAnswers)) {
      const username = session.usernames[userId] || userId;
      breakdown.push({
        userId,
        username,
        answerIndex
      });
    }

    return breakdown.sort((a, b) => a.username.localeCompare(b.username));
  };

  // About Modal Component
  const AboutModal = () => {
    if (!showAboutModal) return null;
    return (
      <div className='modal-overlay' onClick={() => setShowAboutModal(false)} style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
        <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{background: 'white', borderRadius: '10px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto'}}>
          <div className='modal-header'>
            <h2>About PulseRound</h2>
            <button className='modal-close' onClick={() => setShowAboutModal(false)} aria-label='Close'>�</button>
          </div>
          <div className='modal-body'>
            <div className='about-section'>
              <h3>What is PulseRound?</h3>
              <p>PulseRound is a real-time collaborative quiz platform that lets groups create, answer, and discuss questions together. Perfect for classrooms, team meetings, workshops, and social gatherings.</p>
            </div>
            <div className='about-section'>
              <h3>How to Use</h3>
              <div className='usage-steps'>
                <div className='step'>
                  <h4>?? Create a Session</h4>
                  <p>Start a new quiz session and share the session ID with your group.</p>
                </div>
                <div className='step'>
                  <h4>?? Add Questions</h4>
                  <p>Create multiple-choice questions manually or generate them automatically with different tones.</p>
                </div>
                <div className='step'>
                  <h4>?? Join & Answer</h4>
                  <p>Participants join using the session ID and answer questions in real-time.</p>
                </div>
                <div className='step'>
                  <h4>?? Reveal Results</h4>
                  <p>The facilitator can reveal answer counts and correct questions when ready.</p>
                </div>
                <div className='step'>
                  <h4>?? View Analytics</h4>
                  <p>See visual charts of group responses and detailed breakdowns.</p>
                </div>
              </div>
            </div>
            <div className='about-section'>
              <h3>Key Features</h3>
              <ul className='feature-list'>
                <li><strong>Real-time Collaboration:</strong> See responses as they happen</li>
                <li><strong>Automatic Question Generation:</strong> Create questions with different tones (humorous, factual, engaging, challenging)</li>
                <li><strong>Per-Question Control:</strong> Reveal answers for individual questions when you're ready</li>
                <li><strong>Visual Analytics:</strong> Charts and graphs showing group responses</li>
                <li><strong>No Login Required:</strong> Anyone can join with just a session ID</li>
                <li><strong>Mobile Friendly:</strong> Works perfectly on phones, tablets, and desktops</li>
              </ul>
            </div>
            <div className='about-section'>
              <h3>Perfect For</h3>
              <div className='use-cases'>
                <div className='use-case'>
                  <h4>?? Education</h4>
                  <p>Classroom quizzes, knowledge checks, and interactive learning</p>
                </div>
                <div className='use-case'>
                  <h4>?? Business</h4>
                  <p>Team meetings, training sessions, and feedback collection</p>
                </div>
                <div className='use-case'>
                  <h4>?? Events</h4>
                  <p>Icebreakers, audience engagement, and interactive presentations</p>
                </div>
                <div className='use-case'>
                  <h4>?? Social</h4>
                  <p>Family game nights, friend gatherings, and party games</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
        {connected ? 'Connected' : 'Disconnected'}
      </div>

      <header className="header">
        <div className="header-content">
          <img src={process.env.PUBLIC_URL + "/pulseround_logo.png"} alt="PulseRound Logo" className="logo" />
          <div className="header-text">
            <h1>{session ? (session.title || 'Untitled Quiz Session') : 'PulseRound'}</h1>
            <p>Answer questions together in real-time!</p>
          </div>
        </div>
      </header>

      {error && <div className="error">{error}</div>}
      {!session ? (
        <div className="session-form">
          <h2>Join or Create a Session</h2>
          <div>
            <h3>Create New Session:</h3>
            <input
              type="text"
              placeholder="Enter session title (optional)"
              value={sessionTitle}
              onChange={(e) => setSessionTitle(e.target.value)}
              style={{ marginBottom: '10px', width: '300px', padding: '8px' }}
            />
            <br />
            <button onClick={createSession} disabled={loading}>
              {loading ? 'Creating...' : 'Create New Session'}
            </button>
          </div>
          <div>
            <h3>Or join existing:</h3>
            <input
              type="text"
              placeholder="Enter session ID"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
            />
            <button onClick={joinSession} disabled={loading}>
              {loading ? 'Joining...' : 'Join Session'}
            </button>
          </div>
        </div>
      ) : (
        <div className="quiz-container">
          {isCreator() && (
            <div className="question-management">
              <h3>Question Management</h3>
              {!showQuestionForm ? (
                <button
                  onClick={() => setShowQuestionForm(true)}
                  className="add-question-btn"
                >
                  Add New Question
                </button>
              ) : (
                <div className="question-form">
                  <h4>{editingQuestion ? 'Edit Question' : 'Add New Question'}</h4>

                  {/* AI Question Generation */}
                  <div className="ai-generation-section">
                    <div className="form-group">
                      <label>Generate Question with AI:</label>
                      <div className="ai-input-group">
                        <input
                          type="text"
                          value={aiTopic}
                          onChange={(e) => setAiTopic(e.target.value)}
                          placeholder="Enter a topic (e.g., 'World History', 'Biology', 'Programming')"
                          disabled={isGeneratingAI}
                        />
                        <button
                          onClick={generateQuestionWithAI}
                          disabled={isGeneratingAI || !aiTopic.trim()}
                          className="ai-generate-btn"
                        >
                          {isGeneratingAI ? 'Generating...' : 'Generate'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Question:</label>
                    <div className="quill-question">
                      <ReactQuill
                        key={`question-${aiGenerationKey}`}
                        value={questionForm.question}
                        onChange={(value) => {
                          console.log('Question changed:', value);
                          setQuestionForm({ ...questionForm, question: value });
                        }}
                        placeholder="Enter your question"
                        modules={quillModulesWithOptions}
                        formats={quillFormats}
                        theme="snow"
                        tabIndex={0}
                        ref={questionQuillRef} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Options:</label>
                    {questionForm.options.map((option, index) => (
                      <div key={index} className="option-editor">
                        <label>Option {index + 1}:</label>
                        <ReactQuill
                          key={`option-${index}-${aiGenerationKey}`}
                          value={option}
                          onChange={(value) => {
                            console.log(`Option ${index + 1} changed:`, value);
                            const newOptions = [...questionForm.options];
                            newOptions[index] = value;
                            setQuestionForm({ ...questionForm, options: newOptions });
                          }}
                          placeholder={`Option ${index + 1}`}
                          modules={quillModulesWithOptions}
                          formats={quillFormats}
                          theme="snow"
                          tabIndex={index + 1}
                          ref={(el) => {
                            optionQuillRefs.current[index] = el;
                          }} />
                      </div>
                    ))}
                  </div>
                  <div className="form-group">
                    <label>Correct Answer:</label>
                    <select
                      value={questionForm.answer}
                      onChange={(e) => setQuestionForm({ ...questionForm, answer: parseInt(e.target.value) })}
                    >
                      <option value={0}>Option 1</option>
                      <option value={1}>Option 2</option>
                      <option value={2}>Option 3</option>
                      <option value={3}>Option 4</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={questionForm.triviaMode}
                        onChange={(e) => setQuestionForm({ ...questionForm, triviaMode: e.target.checked })}
                      />
                      Trivia Mode (hide vote counts, prevent answer changes)
                    </label>
                  </div>
                  <div className="form-actions">
                    <button
                      onClick={editingQuestion ? updateQuestion : addQuestion}
                      className="save-btn"
                    >
                      {editingQuestion ? 'Update' : 'Add'} Question
                    </button>
                    <button
                      onClick={resetQuestionForm}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Questions list - always visible for admins */}
              {session.questions && session.questions.length > 0 && (
                <div className="questions-list">
                  <h4>Current Questions:</h4>
                  {session.questions.map((question, index) => {
                    const answerCounts = getAnswerCounts(question.id);
                    const userAnswer = getUserAnswer(question.id);

                    // About Modal Component
  const AboutModal = () => {
    if (!showAboutModal) return null;
    return (
      <div className='modal-overlay' onClick={() => setShowAboutModal(false)} style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
        <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{background: 'white', borderRadius: '10px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto'}}>
          <div className='modal-header'>
            <h2>About PulseRound</h2>
            <button className='modal-close' onClick={() => setShowAboutModal(false)} aria-label='Close'>�</button>
          </div>
          <div className='modal-body'>
            <div className='about-section'>
              <h3>What is PulseRound?</h3>
              <p>PulseRound is a real-time collaborative quiz platform that lets groups create, answer, and discuss questions together. Perfect for classrooms, team meetings, workshops, and social gatherings.</p>
            </div>
            <div className='about-section'>
              <h3>How to Use</h3>
              <div className='usage-steps'>
                <div className='step'>
                  <h4>?? Create a Session</h4>
                  <p>Start a new quiz session and share the session ID with your group.</p>
                </div>
                <div className='step'>
                  <h4>?? Add Questions</h4>
                  <p>Create multiple-choice questions manually or generate them automatically with different tones.</p>
                </div>
                <div className='step'>
                  <h4>?? Join & Answer</h4>
                  <p>Participants join using the session ID and answer questions in real-time.</p>
                </div>
                <div className='step'>
                  <h4>?? Reveal Results</h4>
                  <p>The facilitator can reveal answer counts and correct questions when ready.</p>
                </div>
                <div className='step'>
                  <h4>?? View Analytics</h4>
                  <p>See visual charts of group responses and detailed breakdowns.</p>
                </div>
              </div>
            </div>
            <div className='about-section'>
              <h3>Key Features</h3>
              <ul className='feature-list'>
                <li><strong>Real-time Collaboration:</strong> See responses as they happen</li>
                <li><strong>Automatic Question Generation:</strong> Create questions with different tones (humorous, factual, engaging, challenging)</li>
                <li><strong>Per-Question Control:</strong> Reveal answers for individual questions when you're ready</li>
                <li><strong>Visual Analytics:</strong> Charts and graphs showing group responses</li>
                <li><strong>No Login Required:</strong> Anyone can join with just a session ID</li>
                <li><strong>Mobile Friendly:</strong> Works perfectly on phones, tablets, and desktops</li>
              </ul>
            </div>
            <div className='about-section'>
              <h3>Perfect For</h3>
              <div className='use-cases'>
                <div className='use-case'>
                  <h4>?? Education</h4>
                  <p>Classroom quizzes, knowledge checks, and interactive learning</p>
                </div>
                <div className='use-case'>
                  <h4>?? Business</h4>
                  <p>Team meetings, training sessions, and feedback collection</p>
                </div>
                <div className='use-case'>
                  <h4>?? Events</h4>
                  <p>Icebreakers, audience engagement, and interactive presentations</p>
                </div>
                <div className='use-case'>
                  <h4>?? Social</h4>
                  <p>Family game nights, friend gatherings, and party games</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
                      <div key={question.id} style={{ marginBottom: '40px' }}>
                        <div className="question">
                          <div
                            dangerouslySetInnerHTML={{ __html: question.question }}
                            className="question-content"
                          />
                          {isCreator() && (
                            <div className="question-actions">
                              <button
                                onClick={() => startEditQuestion(question)}
                                className="edit-btn"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deleteQuestion(question.id)}
                                className="delete-btn"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="options">
                          {question.options.filter(opt => opt.trim()).map((option, optionIndex) => {
                            const voteCount = answerCounts[optionIndex] || 0;
                            const isThisSelected = userAnswer === optionIndex;

                            // About Modal Component
  const AboutModal = () => {
    if (!showAboutModal) return null;
    return (
      <div className='modal-overlay' onClick={() => setShowAboutModal(false)} style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
        <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{background: 'white', borderRadius: '10px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto'}}>
          <div className='modal-header'>
            <h2>About PulseRound</h2>
            <button className='modal-close' onClick={() => setShowAboutModal(false)} aria-label='Close'>�</button>
          </div>
          <div className='modal-body'>
            <div className='about-section'>
              <h3>What is PulseRound?</h3>
              <p>PulseRound is a real-time collaborative quiz platform that lets groups create, answer, and discuss questions together. Perfect for classrooms, team meetings, workshops, and social gatherings.</p>
            </div>
            <div className='about-section'>
              <h3>How to Use</h3>
              <div className='usage-steps'>
                <div className='step'>
                  <h4>?? Create a Session</h4>
                  <p>Start a new quiz session and share the session ID with your group.</p>
                </div>
                <div className='step'>
                  <h4>?? Add Questions</h4>
                  <p>Create multiple-choice questions manually or generate them automatically with different tones.</p>
                </div>
                <div className='step'>
                  <h4>?? Join & Answer</h4>
                  <p>Participants join using the session ID and answer questions in real-time.</p>
                </div>
                <div className='step'>
                  <h4>?? Reveal Results</h4>
                  <p>The facilitator can reveal answer counts and correct questions when ready.</p>
                </div>
                <div className='step'>
                  <h4>?? View Analytics</h4>
                  <p>See visual charts of group responses and detailed breakdowns.</p>
                </div>
              </div>
            </div>
            <div className='about-section'>
              <h3>Key Features</h3>
              <ul className='feature-list'>
                <li><strong>Real-time Collaboration:</strong> See responses as they happen</li>
                <li><strong>Automatic Question Generation:</strong> Create questions with different tones (humorous, factual, engaging, challenging)</li>
                <li><strong>Per-Question Control:</strong> Reveal answers for individual questions when you're ready</li>
                <li><strong>Visual Analytics:</strong> Charts and graphs showing group responses</li>
                <li><strong>No Login Required:</strong> Anyone can join with just a session ID</li>
                <li><strong>Mobile Friendly:</strong> Works perfectly on phones, tablets, and desktops</li>
              </ul>
            </div>
            <div className='about-section'>
              <h3>Perfect For</h3>
              <div className='use-cases'>
                <div className='use-case'>
                  <h4>?? Education</h4>
                  <p>Classroom quizzes, knowledge checks, and interactive learning</p>
                </div>
                <div className='use-case'>
                  <h4>?? Business</h4>
                  <p>Team meetings, training sessions, and feedback collection</p>
                </div>
                <div className='use-case'>
                  <h4>?? Events</h4>
                  <p>Icebreakers, audience engagement, and interactive presentations</p>
                </div>
                <div className='use-case'>
                  <h4>?? Social</h4>
                  <p>Family game nights, friend gatherings, and party games</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
                              <div
                                key={optionIndex}
                                className={`option ${isThisSelected ? 'selected' : ''} ${getUserAnswer(question.id) === optionIndex ? 'your-answer' : ''}`}
                                onClick={() => submitAnswer(question.id, optionIndex)}
                              >
                                {!question.triviaMode && voteCount > 0 && (
                                  <div className="vote-count">{voteCount}</div>
                                )}
                                {question.triviaMode && getUserAnswer(question.id) !== null && (
                                  <>
                                    {voteCount > 0 && (
                                      <div className="vote-count">{voteCount}</div>
                                    )}
                                    <div className={`correct-answer ${optionIndex === question.answer ? 'correct' : 'incorrect'}`}>
                                      {optionIndex === question.answer ? '✓ Correct' : ''}
                                    </div>
                                  </>
                                )}
                                <div
                                  dangerouslySetInnerHTML={{ __html: option }}
                                  className="option-content"
                                />
                                {isThisSelected && (
                                  <div className="your-answer">Your Answer</div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {isCreator() && (
                          <div className="answer-breakdown">
                            <h4>Answer Breakdown:</h4>
                            {getAnswerBreakdown(question.id).length > 0 ? (
                              getAnswerBreakdown(question.id).map((answer, index) => (
                                <div key={index} className="breakdown-item">
                                  <span className="username">{answer.username}</span>
                                  <span
                                    className="answer-text"
                                    dangerouslySetInnerHTML={{
                                      __html: question.options[answer.answerIndex] || `Option ${answer.answerIndex + 1}`
                                    }}
                                  />
                                </div>
                              ))
                            ) : (
                              <p>No answers yet.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!isCreator() && session.questions && session.questions.length > 0 ? (
            session.questions.map((question, index) => {
              const answerCounts = getAnswerCounts(question.id);

              // About Modal Component
  const AboutModal = () => {
    if (!showAboutModal) return null;
    return (
      <div className='modal-overlay' onClick={() => setShowAboutModal(false)} style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
        <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{background: 'white', borderRadius: '10px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto'}}>
          <div className='modal-header'>
            <h2>About PulseRound</h2>
            <button className='modal-close' onClick={() => setShowAboutModal(false)} aria-label='Close'>�</button>
          </div>
          <div className='modal-body'>
            <div className='about-section'>
              <h3>What is PulseRound?</h3>
              <p>PulseRound is a real-time collaborative quiz platform that lets groups create, answer, and discuss questions together. Perfect for classrooms, team meetings, workshops, and social gatherings.</p>
            </div>
            <div className='about-section'>
              <h3>How to Use</h3>
              <div className='usage-steps'>
                <div className='step'>
                  <h4>?? Create a Session</h4>
                  <p>Start a new quiz session and share the session ID with your group.</p>
                </div>
                <div className='step'>
                  <h4>?? Add Questions</h4>
                  <p>Create multiple-choice questions manually or generate them automatically with different tones.</p>
                </div>
                <div className='step'>
                  <h4>?? Join & Answer</h4>
                  <p>Participants join using the session ID and answer questions in real-time.</p>
                </div>
                <div className='step'>
                  <h4>?? Reveal Results</h4>
                  <p>The facilitator can reveal answer counts and correct questions when ready.</p>
                </div>
                <div className='step'>
                  <h4>?? View Analytics</h4>
                  <p>See visual charts of group responses and detailed breakdowns.</p>
                </div>
              </div>
            </div>
            <div className='about-section'>
              <h3>Key Features</h3>
              <ul className='feature-list'>
                <li><strong>Real-time Collaboration:</strong> See responses as they happen</li>
                <li><strong>Automatic Question Generation:</strong> Create questions with different tones (humorous, factual, engaging, challenging)</li>
                <li><strong>Per-Question Control:</strong> Reveal answers for individual questions when you're ready</li>
                <li><strong>Visual Analytics:</strong> Charts and graphs showing group responses</li>
                <li><strong>No Login Required:</strong> Anyone can join with just a session ID</li>
                <li><strong>Mobile Friendly:</strong> Works perfectly on phones, tablets, and desktops</li>
              </ul>
            </div>
            <div className='about-section'>
              <h3>Perfect For</h3>
              <div className='use-cases'>
                <div className='use-case'>
                  <h4>?? Education</h4>
                  <p>Classroom quizzes, knowledge checks, and interactive learning</p>
                </div>
                <div className='use-case'>
                  <h4>?? Business</h4>
                  <p>Team meetings, training sessions, and feedback collection</p>
                </div>
                <div className='use-case'>
                  <h4>?? Events</h4>
                  <p>Icebreakers, audience engagement, and interactive presentations</p>
                </div>
                <div className='use-case'>
                  <h4>?? Social</h4>
                  <p>Family game nights, friend gatherings, and party games</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
                <div key={question.id} style={{ marginBottom: '40px' }}>
                  <div className="question">
                    <strong>Question {index + 1}:</strong>
                    <div
                      dangerouslySetInnerHTML={{ __html: question.question }}
                      className="question-content"
                    />
                  </div>

                  <div className="options">
                    {question.options.filter(opt => opt.trim()).map((option, optionIndex) => {
                      const voteCount = answerCounts[optionIndex] || 0;
                      const userAnswer = getUserAnswer(question.id);
                      const isThisSelected = userAnswer === optionIndex;

                      // About Modal Component
  const AboutModal = () => {
    if (!showAboutModal) return null;
    return (
      <div className='modal-overlay' onClick={() => setShowAboutModal(false)} style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
        <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{background: 'white', borderRadius: '10px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto'}}>
          <div className='modal-header'>
            <h2>About PulseRound</h2>
            <button className='modal-close' onClick={() => setShowAboutModal(false)} aria-label='Close'>�</button>
          </div>
          <div className='modal-body'>
            <div className='about-section'>
              <h3>What is PulseRound?</h3>
              <p>PulseRound is a real-time collaborative quiz platform that lets groups create, answer, and discuss questions together. Perfect for classrooms, team meetings, workshops, and social gatherings.</p>
            </div>
            <div className='about-section'>
              <h3>How to Use</h3>
              <div className='usage-steps'>
                <div className='step'>
                  <h4>?? Create a Session</h4>
                  <p>Start a new quiz session and share the session ID with your group.</p>
                </div>
                <div className='step'>
                  <h4>?? Add Questions</h4>
                  <p>Create multiple-choice questions manually or generate them automatically with different tones.</p>
                </div>
                <div className='step'>
                  <h4>?? Join & Answer</h4>
                  <p>Participants join using the session ID and answer questions in real-time.</p>
                </div>
                <div className='step'>
                  <h4>?? Reveal Results</h4>
                  <p>The facilitator can reveal answer counts and correct questions when ready.</p>
                </div>
                <div className='step'>
                  <h4>?? View Analytics</h4>
                  <p>See visual charts of group responses and detailed breakdowns.</p>
                </div>
              </div>
            </div>
            <div className='about-section'>
              <h3>Key Features</h3>
              <ul className='feature-list'>
                <li><strong>Real-time Collaboration:</strong> See responses as they happen</li>
                <li><strong>Automatic Question Generation:</strong> Create questions with different tones (humorous, factual, engaging, challenging)</li>
                <li><strong>Per-Question Control:</strong> Reveal answers for individual questions when you're ready</li>
                <li><strong>Visual Analytics:</strong> Charts and graphs showing group responses</li>
                <li><strong>No Login Required:</strong> Anyone can join with just a session ID</li>
                <li><strong>Mobile Friendly:</strong> Works perfectly on phones, tablets, and desktops</li>
              </ul>
            </div>
            <div className='about-section'>
              <h3>Perfect For</h3>
              <div className='use-cases'>
                <div className='use-case'>
                  <h4>?? Education</h4>
                  <p>Classroom quizzes, knowledge checks, and interactive learning</p>
                </div>
                <div className='use-case'>
                  <h4>?? Business</h4>
                  <p>Team meetings, training sessions, and feedback collection</p>
                </div>
                <div className='use-case'>
                  <h4>?? Events</h4>
                  <p>Icebreakers, audience engagement, and interactive presentations</p>
                </div>
                <div className='use-case'>
                  <h4>?? Social</h4>
                  <p>Family game nights, friend gatherings, and party games</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
                        <div
                          key={optionIndex}
                          className={`option ${isThisSelected ? 'selected' : ''}`}
                          onClick={() => submitAnswer(question.id, optionIndex)}
                        >
                          {!question.triviaMode && voteCount > 0 && (
                            <div className="vote-count">{voteCount}</div>
                          )}
                          {question.triviaMode && getUserAnswer(question.id) !== null && (
                            <>
                              {voteCount > 0 && (
                                <div className="vote-count">{voteCount}</div>
                              )}
                              <div className={`correct-answer ${optionIndex === question.answer ? 'correct' : 'incorrect'}`}>
                                {optionIndex === question.answer ? '✓ Correct' : ''}
                              </div>
                            </>
                          )}
                          <div
                            dangerouslySetInnerHTML={{ __html: option }}
                            className="option-content"
                          />
                          {isThisSelected && (
                            <div className="your-answer">Your Answer</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : !isCreator() && (!session.questions || session.questions.length === 0) ? (
            <div className="no-questions">
              <p>Waiting for the session creator to add questions.</p>
            </div>
          ) : null}

          <div className="participants">
            <h3>Active Participants: {getParticipantCount()}</h3>
            <p>See real-time vote counts above each option!</p>
          </div>

          <div className="session-info">
            <div className="session-title">
              {!isEditingTitle ? (
                <h2
                  className={isCreator() ? "editable-title" : "title-display"}
                  onClick={isCreator() ? startEditTitle : undefined}
                >
                  {session.title || 'Untitled Quiz Session'}
                  {isCreator() && <span className="edit-hint"> (click to edit)</span>}
                </h2>
              ) : (
                <div className="title-edit">
                  <input
                    type="text"
                    value={sessionTitle}
                    onChange={(e) => setSessionTitle(e.target.value)}
                    placeholder="Enter session title"
                    onKeyPress={(e) => e.key === 'Enter' && saveTitle()}
                    autoFocus
                  />
                  <button onClick={saveTitle} className="save-btn-small">Save</button>
                  <button onClick={cancelEditTitle} className="cancel-btn-small">Cancel</button>
                </div>
              )}
            </div>
            <h3>
              Session: <a
                href={getShareableLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="session-link"
                title="Click to open session link in new tab"
              >
                {session.id}
              </a>
            </h3>
            <div className="shareable-link">
              <label>Share this link:</label>
              <div className="link-container">
                <input
                  type="text"
                  value={getShareableLink()}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(getShareableLink());
                    alert('Link copied to clipboard!');
                  }}
                  className="copy-btn"
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="user-id-section">
              {!isEditingUsername ? (
                <p className="user-id-display" onClick={startEditUsername}>
                  Your ID: {username || userId}
                  <span className="edit-hint"> (click to edit)</span>
                </p>
              ) : (
                <div className="username-edit">
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    onKeyDown={(e) => e.key === 'Enter' && saveUsername()}
                    autoFocus
                  />
                  <button onClick={saveUsername} className="save-btn-small">Save</button>
                  <button onClick={cancelEditUsername} className="cancel-btn-small">Cancel</button>
                </div>
              )}
            </div>
            <p>Participants: {getParticipantCount()}</p>
            {isCreator() && (
              <div className="creator-controls">
                <p className="creator-badge">You are the creator</p>
                <div className="reveal-answers-toggle">
                  <label>
                    <input
                      type="checkbox"
                      checked={session?.revealCorrectAnswers || false}
                      onChange={toggleRevealAnswers}
                    />
                    Reveal Correct Answers (for trivia mode questions)
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      {/* User Sessions History - Always visible */}
      {userSessions.length > 0 && (
        <div className="user-sessions">
          <h3>Your Quiz Sessions</h3>
          <div className="sessions-list">
            {userSessions.map((session, index) => (
              <div key={session.id} className="session-item">
                <div className="session-info">
                  <h4>{session.title}</h4>
                  <p>Created: {new Date(session.createdAt).toLocaleString()}; Last Accessed: {new Date(session.lastAccessed).toLocaleString()}</p>
                </div>
                <div className="session-actions">
                  <button
                    onClick={() => window.location.href = `?session=${session.id}`}
                    className="join-session-btn"
                  >
                    Join
                  </button>
                  <button
                    onClick={() => removeSessionFromHistory(session.id)}
                    className="remove-session-btn"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Floating Create Session Button */}
      <div className="floating-create-session">
        <button onClick={createSession} className="add-session-btn">
          <span className="btn-text">Create New Session</span>
        </button>
      </div>

    </div>
  )
}



export default App;
